import React from "react";
import { Msr } from "./components";
import { money } from "./quoteRows";
import "./t5.css";

// T5 — Step 3: Assessment (reclaim allocation), conditional on the event
// charging a school assessment (acceptance D1). The school's assessments form
// a pool; the teacher allocates it toward the event's reclaim items (label +
// amount, admin-configured). Unallocated remainder auto-becomes a SPONSORSHIP
// (D2 — the minimum is always met); allocating beyond the pool adds the
// overage to the checkout total (D3).
//
// Pool/prior figures come from the server quote (breakdown.reclaim) — this
// screen only does live display math on top; the engine recomputes everything
// at checkout (E1). No artwork uploads, no submission deadlines (§14 —
// off-platform).

function QtyStepper({ value, onChange }) {
  // The count is typeable (schools order 30 céilí tickets — that can't be 30
  // clicks). While the field is focused the draft may be "" without collapsing
  // to a 0 under the caret; blur commits the parent value back into view.
  const [draft, setDraft] = React.useState(null);
  const type = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 3);
    setDraft(digits);
    onChange(digits === "" ? 0 : Math.min(999, parseInt(digits, 10)));
  };
  return (
    <div className="qstep">
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} disabled={value === 0} aria-label="Decrease">
        <Msr>remove</Msr>
      </button>
      <input
        className="qty"
        type="text"
        inputMode="numeric"
        aria-label="Quantity"
        value={draft != null ? draft : String(value)}
        onChange={(e) => type(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => setDraft(null)}
      />
      <button type="button" onClick={() => onChange(Math.min(999, value + 1))} aria-label="Increase">
        <Msr>add</Msr>
      </button>
    </div>
  );
}

export default function Assessment({ event, quote, quoting, reclaimRows, onChangeReclaim }) {
  const items = (event && event.reclaimItems) || [];
  const perDancer = (event && event.fees && event.fees.schoolAssessmentPerDancer) || 0;
  const reclaim = quote && quote.breakdown && quote.breakdown.reclaim;

  const qtyOf = (key) => {
    const row = reclaimRows.find((r) => r.itemKey === key);
    return row ? row.qty : 0;
  };
  const setQty = (key, qty) => {
    const rest = reclaimRows.filter((r) => r.itemKey !== key);
    onChangeReclaim(qty > 0 ? [...rest, { itemKey: key, qty }] : rest);
  };

  // live math on top of the quoted pool (cents)
  const poolCents = reclaim ? reclaim.poolCents : 0;
  const assessedCount = reclaim ? reclaim.assessedCount : 0;
  const priorAllocatedCents = reclaim
    ? reclaim.allocatedCents - reclaim.newAllocatedCents
    : 0;
  // prior overage already charged = raw excess at quote time minus what the
  // quote said is still due — lets the live "over by" show only NEW money.
  const priorOverageCents = reclaim
    ? Math.max(0, reclaim.allocatedCents - reclaim.poolCents) - reclaim.overageCents
    : 0;
  const newAllocatedCents = items.reduce(
    (s, it) => s + qtyOf(String(it.key)) * Math.round((it.amount || 0) * 100),
    0
  );
  const allocatedCents = priorAllocatedCents + newAllocatedCents;
  const sponsorshipCents = Math.max(0, poolCents - allocatedCents);
  const overageDueCents = Math.max(
    0,
    Math.max(0, allocatedCents - poolCents) - priorOverageCents
  );
  const pct = poolCents > 0 ? Math.min(100, (allocatedCents / poolCents) * 100) : allocatedCents > 0 ? 100 : 0;
  const over = allocatedCents > poolCents;
  const statusText = over
    ? `Over by ${money(overageDueCents)} — added to your total at checkout.`
    : sponsorshipCents > 0
      ? `Pool covered — ${money(sponsorshipCents)} becomes a sponsorship.`
      : "Pool fully allocated.";

  return (
    <div className="t5">
      <div className="pool-note">
        <Msr>savings</Msr>
        <div>
          <div className="pn-main">
            Assessment pool: <b>{quoting ? "…" : money(poolCents)}</b>{" "}
            {!quoting && (
              <span className="pn-calc">
                ({assessedCount} dancer{assessedCount === 1 ? "" : "s"} × {money(Math.round(perDancer * 100))})
              </span>
            )}
          </div>
          <div className="pn-sub">
            Allocate it toward the items below — anything you don't allocate automatically
            becomes a sponsorship to the event.
          </div>
        </div>
      </div>

      <div className="t5-wrap">
        <div className="main">
          <div className="card">
            <div className="lhead">
              <span>Reclaim item</span><span className="r">Unit price</span>
              <span className="c">Quantity</span><span className="r">Line total</span>
            </div>
            {items.length === 0 && (
              <div className="lempty">
                This event has no reclaim items — your assessment becomes a sponsorship.
              </div>
            )}
            {items.map((it) => {
              const key = String(it.key);
              const unitCents = Math.round((it.amount || 0) * 100);
              const qty = qtyOf(key);
              const total = unitCents * qty;
              return (
                <div className={"lrow" + (qty === 0 ? " zero" : "")} key={key}>
                  <div className="lname">{it.label}</div>
                  <div className="lprice">{money(unitCents)}</div>
                  <QtyStepper value={qty} onChange={(q) => setQty(key, q)} />
                  <div className={"ltotal" + (total === 0 ? " zero" : "")}>{money(total)}</div>
                </div>
              );
            })}
            {items.length > 0 && (
              <div className="lfoot">
                <span className="lf-k">Allocated</span><span></span><span></span>
                <span className="lf-v">{money(newAllocatedCents)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="t5-aside">
          <div className="sumcard">
            <h2>Allocation summary</h2>
            <div className="sumbody">
              {priorAllocatedCents > 0 && (
                <div className="srow">
                  <span className="k"><Msr>history</Msr>Previously allocated</span>
                  <span className="v">{money(priorAllocatedCents)}</span>
                </div>
              )}
              <div className="srow">
                <span className="k"><Msr>shopping_bag</Msr>Allocated</span>
                <span className="v">{money(allocatedCents)}</span>
              </div>
              <div className="srow spon">
                <span className="k"><Msr>volunteer_activism</Msr>Sponsorship</span>
                <span className="v">{money(sponsorshipCents)}</span>
              </div>
              <div className="srow pool">
                <span className="k">Assessment pool</span>
                <span className="v">{quoting ? "…" : money(poolCents)}</span>
              </div>

              <div className={"hero" + (over ? " over" : "")}>
                <div className="hero-top">
                  <span className="hero-amt">
                    <b>{money(allocatedCents)}</b> of {money(poolCents)} allocated
                  </span>
                  <span className={"badge-status " + (over ? "over" : "ok")}>
                    <Msr>{over ? "priority_high" : "check"}</Msr>{over ? "Over" : "Covered"}
                  </span>
                </div>
                <div className="bar">
                  <span className={"fill" + (over ? " over" : "")} style={{ width: pct + "%" }}></span>
                </div>
                <div className="hero-status">{statusText}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
